import { AxiosError, AxiosRequestConfig } from "axios"

import { getAuthInfo, removeAuthInfo, saveAuthInfo } from "@/lib/auth"

import axios from "./axios"

/**
 * Data type for `204 No Content` response
 */
export type NoContentType = null

type ReturnType<T> = Promise<T>

export type APIFn<T, P = void> = P extends void
  ? (args: AxiosRequestConfig) => ReturnType<T>
  : (args: AxiosRequestConfig & { urlParams: P }) => ReturnType<T>

export type PaginatedAPIFn<T, P = void> = P extends void
  ? (args: AxiosRequestConfig) => ReturnType<{
      data: T
      total: number
      nextPage: number | null
      prevPage: number | null
    } | null>
  : (args: AxiosRequestConfig & { urlParams: P }) => ReturnType<{
      data: T
      total: number
      nextPage: number | null
      prevPage: number | null
    } | null>

export class APIError extends Error {
  /**
   * True if the server responds with status code 500
   */
  is500: boolean
  /**
   * True if the server responds with status code 404 and content-length = 0
   */
  isEmpty404: boolean
  /**
   * True if either of the following occurs:
   * - There is a network failure on the client
   * - The server is unresponsive
   * - The request timed out
   */
  isNetworkFailure: boolean
  /**
   * Response status code
   */
  statusCode?: number

  /**
   * API endpoint path or URL where the error occurred
   */
  path?: string

  /**
   * Contains the original error returned by axios.
   * Is useful when the consumer would like to access
   * other properties of the response which is very rare
   */
  __errors: AxiosError
  /**
   * Object containing a generic error message and 'key specific' error
   * There will always be one or the other. Not both together
   */
  errors: {
    /**
     * A generic error message describing the error (new structure)
     */
    message?: string
    /**
     * A generic error message describing the error (old structure)
     */
    detail?: string
    /**
     * These string keys contain the name of the key where the error has occurred
     */
    fieldErrors?: {
      [key: string]: string[] | string
    }
  }

  constructor(error: AxiosError) {
    super(`APIError (${error.response?.status}): ${error.config?.url}`)
    this.name = "APIError"

    this.__errors = error
    this.path = error.config?.url

    this.is500 = error.response?.status === 500
    this.isNetworkFailure = error.response === undefined
    this.isEmpty404 =
      error.response?.status === 404 &&
      error.response?.headers["content-length"] === "0"
    this.statusCode = error.response?.status

    let detail = undefined
    let message = undefined
    let fieldErrors = undefined

    const responseData = error.response?.data as any

    if (!this.is500 && !this.isNetworkFailure && !this.isEmpty404) {
      detail = responseData?.errors?.detail ?? null
      if (!detail && error?.response) {
        message = responseData?.errors?.message ?? null
        if (message) delete responseData?.errors?.message
        fieldErrors = responseData?.errors ?? null
      }
    }

    this.errors = {
      fieldErrors,
      message,
      detail,
    }
  }
}

export const isAxiosError = (error: unknown): error is AxiosError => {
  if (typeof error === "object" && error !== null) {
    if ("isAxiosError" in error) {
      // @ts-expect-error Disabling is safe here. There is a manual check for the property on the line above.
      return error.isAxiosError
    }
  }
  return false
}

const forceLogin = () => {
  removeAuthInfo()
  window.location.reload()
}

let isTokenRefreshing = false
const promises: (() => void)[] = []

export async function refreshToken(): Promise<boolean> {
  const authInfo = getAuthInfo()
  if (!authInfo) {
    return false
  }

  if (isTokenRefreshing) {
    // getting reference to promise's resolve method so we can call it from outside
    let resolvePromise: any
    const currentRequestPromise = new Promise<boolean>((resolve) => {
      resolvePromise = resolve
    })
    // pushing all the promises in the array so they can be resolved later
    promises.push(() => resolvePromise())

    // waiting for the new refresh token from first api call
    return await currentRequestPromise
  }

  isTokenRefreshing = true

  let refreshed = false
  try {
    const refreshTokenValue = authInfo.refreshToken

    if (!refreshTokenValue) throw new Error("User is already logged out")

    // Direct axios call to avoid circular dependency
    const response = await axios.post(
      "/solis/v1/auth/refresh-token/",
      { refreshToken: refreshTokenValue },
      {
        headers: {
          Authorization: null,
        },
      },
    )

    const data = response.data
    const newAuthInfo = {
      ...authInfo,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    }
    saveAuthInfo(newAuthInfo)
    refreshed = true
  } catch (e) {
    console.info("> Refresh token expired. Logging out.", e)
  }

  // resolving all the pending promises so awaited code can resume execution
  isTokenRefreshing = false
  promises.splice(0).forEach((callBack) => {
    callBack()
  })

  if (!refreshed) {
    forceLogin()
  }

  return refreshed
}

/**
 * Converts object keys to a specific format specified by the transformer function recursively
 */
const transformKeys = (obj: any, transformer: (arg: string) => string): any => {
  if (Array.isArray(obj)) {
    return obj.map((v) => transformKeys(v, transformer))
  } else if (obj != null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      return {
        ...result,
        /**
         * There are cases when object keys are UUIDs
         * In our previous setup they were getting converted into
         * camelCase, which is definitely not wanted, therefore this check
         * Example for this --> AllAssignments API call returns such obj
         */
        [isUUIDString(key) ? key : transformer(key)]: transformKeys(
          obj[key],
          transformer,
        ),
      }
    }, {})
  }
  return obj
}

/**
 * Override axios's default implementation of serializing query params
 * we allow the following:
 * ?status=0&status=1
 */
function paramsSerializer(params = {}): string {
  const arr: string[] = []
  for (const [key, value] of Object.entries(params)) {
    if (value === null) continue

    if (Array.isArray(value)) {
      value.forEach((v) => arr.push(`${key}=${v}`))
    } else {
      arr.push(`${key}=${value}`)
    }
  }
  return arr.join("&")
}

export { paramsSerializer, transformKeys }

function isUUIDString(str: string) {
  const UUIDv4Regex =
    /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i
  return UUIDv4Regex.test(str)
}
